import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUserPen } from '@fortawesome/free-solid-svg-icons';
import { useProfile, ProfileInfo } from '../../context/ProfileContext';
import './ProfileSwitcher.css';

const AVATAR_COLORS = [
  '#06b6d4', // teal
  '#8b5cf6', // purple
  '#f43f5e', // rose
  '#f59e0b', // amber
  '#10b981', // emerald
  '#6366f1', // indigo
];

const getInitial = (name: string): string => {
  return name.trim().charAt(0).toUpperCase() || '?';
};

const getAvatarColor = (index: number): string => {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
};

const ProfileSwitcher: React.FC = () => {
  const { profiles, isProfileSelected, switchProfile } = useProfile();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  const handleSelect = useCallback(
    (profile: ProfileInfo) => {
      setExiting(true);
      setTimeout(() => {
        switchProfile(profile);
      }, 300);
    },
    [switchProfile]
  );

  const handleManageProfiles = useCallback(() => {
    // Auto-select self first so the overlay closes
    const selfProfile = profiles.find((p) => p.type === 'self');
    if (selfProfile) {
      switchProfile(selfProfile);
    }
    navigate('/profile#family');
  }, [profiles, switchProfile, navigate]);

  // Don't render if profile is already selected or no profiles loaded
  if (isProfileSelected) {
    return null;
  }

  // Loading state while profiles are being fetched
  if (profiles.length === 0) {
    return (
      <div className="profile-switcher-overlay" role="dialog" aria-label="Loading profiles">
        <div className="profile-switcher-loading">
          <div className="profile-switcher-spinner" />
          <span>Loading profiles...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`profile-switcher-overlay ${exiting ? 'exiting' : ''}`}
      role="dialog"
      aria-label="Select a profile"
      aria-modal="true"
    >
      {/* Logo */}
      <div className="profile-switcher-logo">
        <img src="/images/logo.svg" alt="Infinicare" />
      </div>

      {/* Title */}
      <h1 className="profile-switcher-title">Who's viewing?</h1>

      {/* Profile Cards */}
      <div className="profile-switcher-cards">
        {profiles.map((profile, index) => (
          <button
            key={String(profile.id)}
            className="profile-card"
            onClick={() => handleSelect(profile)}
            tabIndex={0}
            aria-label={`Switch to ${profile.name}${profile.relationship ? ` (${profile.relationship})` : ''}`}
          >
            <div
              className="profile-card-avatar"
              style={{ backgroundColor: getAvatarColor(index) }}
            >
              {profile.avatar ? (
                <img src={profile.avatar} alt={profile.name} />
              ) : (
                getInitial(profile.name)
              )}
              {profile.type === 'self' && (
                <span className="profile-card-self-badge">
                  <FontAwesomeIcon icon={faUser} />
                </span>
              )}
            </div>
            <div className="profile-card-details">
              <span className="profile-card-name">{profile.name}</span>
              {profile.relationship && (
                <span className="profile-card-relationship">{profile.relationship}</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Manage Profiles */}
      <div className="profile-switcher-manage">
        <button onClick={handleManageProfiles} tabIndex={0}>
          <FontAwesomeIcon icon={faUserPen} />
          Manage Profiles
        </button>
      </div>
    </div>
  );
};

export default ProfileSwitcher;
